import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, ArrowRight, Rocket } from 'lucide-react';
import Button from '@/components/ui/Button';

const HeroSection = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center justify-center pt-24 pb-16 overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-white to-slate-50" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(142,227,205,0.3)_0%,rgba(255,255,255,0)_60%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_80%,rgba(155,135,245,0.2)_0%,rgba(255,255,255,0)_60%)]" />
      
      {/* Dotted pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(#2b825b 1px, transparent 1px)',
        backgroundSize: '20px 20px'
      }} />

      <div className="container px-4 mx-auto relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                Arrêtez d'investir à l'aveugle. <span className="text-orcsys-500">Testez d'abord</span> votre idée.
              </h1>
              
              <p className="text-lg text-slate-600 mb-8">
                Vous n'avez pas besoin de 50 000 € pour savoir si votre concept tient la route.
                Avec Prototype, nous transformons votre idée en un produit testable, crédible et convaincant —
                en quelques semaines et sans explosion de budget.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button.Link 
                  href="#contact" 
                  size="lg" 
                  className="bg-orcsys-500 hover:bg-orcsys-600 text-white group w-full sm:min-w-[280px] justify-center whitespace-nowrap"
                >
                  <span className="flex items-center justify-center px-4">
                    Discutons de votre idée gratuitement
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                  </span>
                </Button.Link>
              </div>

              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-orcsys-50 to-orcsys-100 border border-orcsys-200 shadow-sm">
                <Rocket className="w-4 h-4 text-orcsys-500 mr-2" />
                <span className="text-sm font-medium text-orcsys-700">
                  Prototype livré en 2 à 6 semaines
                </span>
              </div>
            </motion.div>

            {/* Prototype Preview */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative mx-auto lg:mx-0 max-w-[320px] lg:max-w-none h-[500px] lg:h-[560px] hidden lg:block"
            >
              <div className="relative h-full">
                <motion.div
                  className="absolute top-0 left-0 w-full h-full"
                  animate={{ 
                    rotate: [-2, 2, -2],
                    y: [0, -5, 0] 
                  }}
                  transition={{ 
                    duration: 6,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: "easeInOut"
                  }}
                >
                  <div className="relative border-[10px] border-slate-800 rounded-[40px] shadow-2xl overflow-hidden 
                    h-[510px] w-[260px] mx-auto"
                  >
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-orcsys-400 to-orcsys-600 opacity-80" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                      <div className="text-center mb-4">
                        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm mx-auto flex items-center justify-center">
                          <Lightbulb className="w-8 h-8" />
                        </div>
                        <h3 className="mt-3 text-xl font-semibold">Votre Prototype</h3>
                        <p className="text-white/80 text-sm">Idée transformée en réalité</p>
                      </div>
                      <div className="w-[80%] h-1 bg-white/20 rounded-full my-2" />
                      <div className="w-[60%] h-1 bg-white/20 rounded-full my-2" />
                      <div className="w-[40%] h-1 bg-white/20 rounded-full my-2" />
                    </div>
                  </div>
                </motion.div>

                {/* Dashboard Preview */}
                <motion.div
                  className="absolute bottom-0 right-0 lg:-right-10 z-10"
                  animate={{ 
                    rotate: [2, -2, 2],
                    y: [0, 5, 0] 
                  }}
                  transition={{ 
                    duration: 7,
                    repeat: Infinity,
                    repeatType: "reverse",
                    ease: "easeInOut"
                  }}
                >
                  <div className="relative border-[10px] border-slate-800 rounded-[40px] shadow-2xl overflow-hidden 
                    h-[450px] w-[230px]"
                  >
                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-slate-700 to-slate-900 opacity-90" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                      <div className="text-center mb-4">
                        <div className="w-14 h-14 rounded-full bg-orcsys-500/20 backdrop-blur-sm mx-auto flex items-center justify-center">
                          <Rocket className="w-6 h-6" />
                        </div>
                        <h3 className="mt-3 text-lg font-semibold">Dashboard</h3>
                        <p className="text-white/80 text-xs">Suivi en temps réel</p>
                      </div>
                      <div className="w-[70%] h-1 bg-white/20 rounded-full my-2" />
                      <div className="w-[50%] h-1 bg-white/20 rounded-full my-2" />
                      <div className="w-[30%] h-1 bg-white/20 rounded-full my-2" />
                    </div>
                  </div>
                </motion.div>

                {/* Decorative elements */}
                <div className="absolute -top-10 -left-10 w-20 h-20 bg-orcsys-500/10 rounded-full blur-xl" />
                <div className="absolute -bottom-10 -right-10 w-20 h-20 bg-orcsys-500/10 rounded-full blur-xl" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;

